import { useState, useEffect } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, Plus, Pencil } from 'lucide-react'
import { createRiskApi, updateRiskApi } from '@frontend/lib/api'
import type { CreateRiskInput } from '@common/schemas'
import type { RiskDetail } from '@common/types'
import { RISK_TREATMENT_PLANS } from '@common/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { TagsInput } from '@frontend/components/shared/TagsInput'

type RiskFormData = CreateRiskInput

interface AddRiskModalProps {
  mode: 'create'
  open: boolean
  onOpenChange: (open: boolean) => void
  risk?: never
}

interface EditRiskModalProps {
  mode: 'edit'
  open: boolean
  onOpenChange: (open: boolean) => void
  risk: RiskDetail
}

type RiskModalProps = AddRiskModalProps | EditRiskModalProps

const emptyFormData: RiskFormData = {
  name: '',
  impact: 1,
  likelihood: 1,
  residualImpact: 1,
  residualLikelihood: 1,
  description: '',
  categories: [],
  treatmentPlan: 'Mitigate',
  treatmentDetails: '',
  controls: [],
}

function riskToFormData(risk: RiskDetail): RiskFormData {
  return {
    name: risk.name,
    impact: risk.impact,
    likelihood: risk.likelihood,
    residualImpact: risk.residualImpact,
    residualLikelihood: risk.residualLikelihood,
    description: risk.description,
    categories: risk.categories,
    treatmentPlan: risk.treatmentPlan,
    treatmentDetails: risk.treatmentDetails,
    controls: risk.controls,
  }
}

const SCALE = [1, 2, 3, 4, 5] as const

export function RiskModal(props: RiskModalProps) {
  const { mode, open, onOpenChange } = props
  const risk = mode === 'edit' ? props.risk : undefined
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<RiskFormData>({ ...emptyFormData })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setFormData(risk ? riskToFormData(risk) : { ...emptyFormData })
      setError(null)
    }
  }, [open, risk])

  const createMutation = useMutation({
    mutationFn: createRiskApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['risks'] })
      onOpenChange(false)
      toast.success(`Risk "${data.risk.name}" created`)
    },
    onError: (err) => handleMutationError(err, 'Failed to create risk'),
  })

  const updateMutation = useMutation({
    mutationFn: (data: RiskFormData) => updateRiskApi(risk!.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['risks'] })
      queryClient.invalidateQueries({ queryKey: ['risk', risk!.id] })
      queryClient.invalidateQueries({ queryKey: ['risk-history', risk!.id] })
      onOpenChange(false)
      toast.success(`Risk "${data.risk.name}" updated`)
    },
    onError: (err) => handleMutationError(err, 'Failed to update risk'),
  })

  function handleMutationError(err: Error, fallback: string) {
    if (isHTTPError(err)) {
      const data = err.data as { error?: string }
      setError(data.error || fallback)
    } else {
      setError(fallback)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    const cleaned: RiskFormData = {
      ...formData,
      name: formData.name.trim(),
    }

    if (mode === 'create') {
      createMutation.mutate(cleaned)
    } else {
      updateMutation.mutate(cleaned)
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isCreate = mode === 'create'

  const score = formData.impact * formData.likelihood
  const residualScore = formData.residualImpact * formData.residualLikelihood

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCreate ? <Plus className="h-5 w-5" /> : <Pencil className="h-5 w-5" />}
            {isCreate ? 'Add Risk' : 'Edit Risk'}
          </DialogTitle>
          <DialogDescription>{isCreate ? 'Add a new risk to the catalog.' : 'Update risk details.'}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="risk-name" className="block text-sm font-medium mb-1">
              Risk Name <span className="text-red-500">*</span>
            </label>
            <input
              id="risk-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Unauthorized data access"
              disabled={isSubmitting}
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="risk-description" className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              id="risk-description"
              value={formData.description ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px]"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="risk-treatment-plan" className="block text-sm font-medium mb-1">
              Treatment Plan
            </label>
            <select
              id="risk-treatment-plan"
              value={formData.treatmentPlan ?? 'Mitigate'}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, treatmentPlan: e.target.value as RiskFormData['treatmentPlan'] }))
              }
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            >
              {RISK_TREATMENT_PLANS.map((plan) => (
                <option key={plan} value={plan}>
                  {plan}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="risk-treatment-details" className="block text-sm font-medium mb-1">
              Treatment Details
            </label>
            <textarea
              id="risk-treatment-details"
              value={formData.treatmentDetails ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, treatmentDetails: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[60px]"
              disabled={isSubmitting}
            />
          </div>

          <fieldset className="border rounded-md p-3 space-y-3">
            <legend className="px-1 text-sm font-medium">Inherent risk</legend>
            <div className="grid grid-cols-3 gap-3">
              <ScaleSelect
                label="Impact"
                value={formData.impact}
                onChange={(impact) => setFormData((prev) => ({ ...prev, impact }))}
                disabled={isSubmitting}
              />
              <ScaleSelect
                label="Likelihood"
                value={formData.likelihood}
                onChange={(likelihood) => setFormData((prev) => ({ ...prev, likelihood }))}
                disabled={isSubmitting}
              />
              <ReadOnlyField label="Score" value={score} />
            </div>
          </fieldset>

          <fieldset className="border rounded-md p-3 space-y-3">
            <legend className="px-1 text-sm font-medium">Residual risk</legend>
            <div className="grid grid-cols-3 gap-3">
              <ScaleSelect
                label="Impact"
                value={formData.residualImpact}
                onChange={(residualImpact) => setFormData((prev) => ({ ...prev, residualImpact }))}
                disabled={isSubmitting}
              />
              <ScaleSelect
                label="Likelihood"
                value={formData.residualLikelihood}
                onChange={(residualLikelihood) => setFormData((prev) => ({ ...prev, residualLikelihood }))}
                disabled={isSubmitting}
              />
              <ReadOnlyField label="Score" value={residualScore} />
            </div>
          </fieldset>

          <TagsInput
            label="Controls"
            values={formData.controls ?? []}
            onChange={(controls) => setFormData((prev) => ({ ...prev, controls }))}
            placeholder="Eden control codes (e.g. CC6.1)..."
            helpText="Eden control codes; resolved to Drata IDs at push time."
            disabled={isSubmitting}
          />

          <TagsInput
            label="Categories"
            values={formData.categories ?? []}
            onChange={(categories) => setFormData((prev) => ({ ...prev, categories }))}
            placeholder="Free-text categories..."
            helpText="Display only — not currently sent to Drata."
            disabled={isSubmitting}
          />

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isCreate ? 'Create Risk' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ScaleSelect({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: number
  onChange: (n: number) => void
  disabled?: boolean
}) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        disabled={disabled}
      >
        {SCALE.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  )
}

function ReadOnlyField({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      <div className="px-2 py-1.5 border rounded-md text-sm bg-muted/40 text-muted-foreground">{value}</div>
    </div>
  )
}
